// ============================================================
// systemLogger.ts — Observabilidade centralizada Construzilla
//
// REGRAS ABSOLUTAS:
//   1. Nunca lança exceção — toda chamada é fire-and-forget silenciosa
//   2. Nunca loga PII: senha, token, CPF, cartão, chave de API
//   3. Nunca cria loop: erro de log não gera outro log
//   4. Não bloqueia o fluxo do usuário (await é seguro mas interno)
//   5. Em development: desabilitado por padrão (VITE_OBSERVE_IN_DEV=true para ativar)
// ============================================================

import { supabase as internalSupabase } from '@/lib/supabase'

// ── Configuração ──────────────────────────────────────────────────
const APP_VERSION = '1.0.0'
const ENV: 'development' | 'staging' | 'production' =
  import.meta.env.MODE === 'development' ? 'development' : 'production'

// Em dev, só persiste se VITE_OBSERVE_IN_DEV=true — evita poluição de logs
const PERSIST_ENABLED =
  ENV !== 'development' || import.meta.env.VITE_OBSERVE_IN_DEV === 'true'

// ── Rate Limiter ──────────────────────────────────────────────────
// Máx. 20 inserts por janela de 60s — evita spam em loop de renderização
// Janela por combinação (module + action) — erros distintos passam normalmente
const RATE_WINDOW_MS = 60_000
const RATE_LIMIT_PER_KEY = 20
const DEDUP_WINDOW_MS = 5_000   // suprime duplicatas exatas em 5s

const _rateCounters = new Map<string, { count: number; windowStart: number }>()
const _lastSeen = new Map<string, number>()   // hash → timestamp

function _makeRateKey(module: string | null, action: string | null): string {
  return `${module ?? '*'}::${action ?? '*'}`
}

function _makeDedupeHash(message: string, module: string | null): string {
  // Hash leve: primeiros 120 chars da mensagem + módulo
  return `${(module ?? '').slice(0, 40)}|${message.slice(0, 120)}`
}

function _isAllowed(message: string, module: string | null, action: string | null): boolean {
  const now = Date.now()

  // 1. Dedupe: mesma mensagem+módulo nos últimos 5s → ignorar
  const dedupeKey = _makeDedupeHash(message, module)
  const lastTs = _lastSeen.get(dedupeKey)
  if (lastTs && now - lastTs < DEDUP_WINDOW_MS) return false
  _lastSeen.set(dedupeKey, now)

  // 2. Rate limit por janela de 60s por chave module+action
  const rateKey = _makeRateKey(module, action)
  const bucket = _rateCounters.get(rateKey)
  if (!bucket || now - bucket.windowStart > RATE_WINDOW_MS) {
    _rateCounters.set(rateKey, { count: 1, windowStart: now })
    return true
  }
  if (bucket.count >= RATE_LIMIT_PER_KEY) return false
  bucket.count++
  return true
}

// ── Tipos ─────────────────────────────────────────────────────────
type Severity = 'low' | 'medium' | 'high' | 'critical'

export interface LogContext {
  page?: string
  module?: string
  action?: string
  severity?: Severity
  errorCode?: string | null
  companyId?: string | null
  obraId?: string | null
  userId?: string | null
  userEmail?: string | null
  endpoint?: string | null
  statusCode?: number | null
  requestPayload?: unknown
  responsePayload?: unknown
}

// ── Sanitização de PII ────────────────────────────────────────────
const SENSITIVE_KEYS = [
  'password', 'senha', 'token', 'access_token', 'refresh_token',
  'authorization', 'secret', 'api_key', 'apikey',
  'cpf', 'rg', 'cnh', 'passport',
  'card_number', 'cardnumber', 'cvv', 'cvc',
  'iban', 'account_number', 'agencia', 'conta_bancaria',
]

function maskSensitive(input: unknown, depth = 0): unknown {
  if (depth > 6 || input == null) return input
  if (Array.isArray(input)) return input.map((v) => maskSensitive(v, depth + 1))
  if (typeof input === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      const lk = k.toLowerCase()
      out[k] = SENSITIVE_KEYS.some((s) => lk.includes(s))
        ? '***MASKED***'
        : maskSensitive(v, depth + 1)
    }
    return out
  }
  // Mascarar tokens em strings brutas (ex: "Bearer eyJ...")
  if (typeof input === 'string') {
    return input.replace(/Bearer\s+\S+/gi, 'Bearer ***MASKED***')
                .replace(/\b(eyJ[\w-]{10,}\.[\w-]{10,}\.[\w-]{5,})\b/g, '***JWT***')
  }
  return input
}

function safeStringifyPayload(payload: unknown): unknown | null {
  if (payload == null) return null
  try {
    const masked = maskSensitive(payload)
    const str = JSON.stringify(masked)
    if (str.length > 32_000) {
      // Trunca mas mantém JSON válido
      return { _truncated: true, _preview: str.slice(0, 500) }
    }
    return masked
  } catch {
    return null
  }
}

// ── Helpers de contexto ───────────────────────────────────────────
function detectDevice(): string {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent
  if (/mobile/i.test(ua)) return 'mobile'
  if (/tablet|ipad/i.test(ua)) return 'tablet'
  return 'desktop'
}

// Cache de usuário por 30s para evitar chamadas auth.getUser() excessivas
let _userCache: { id: string | null; email: string | null; at: number } | null = null

async function getCurrentUser(): Promise<{ id: string | null; email: string | null }> {
  try {
    const now = Date.now()
    if (_userCache && now - _userCache.at < 30_000) {
      return { id: _userCache.id, email: _userCache.email }
    }
    const { data } = await internalSupabase.auth.getUser()
    _userCache = { id: data.user?.id ?? null, email: data.user?.email ?? null, at: now }
    return { id: _userCache.id, email: _userCache.email }
  } catch {
    return { id: null, email: null }
  }
}

function readActiveContextFromDom(): { companyId: string | null; obraId: string | null } {
  try {
    const companyId = window.localStorage.getItem('cz:company_id') || null
    const obraId = window.localStorage.getItem('cz:obra_id') || null
    return { companyId, obraId }
  } catch {
    return { companyId: null, obraId: null }
  }
}

// ── Função principal ──────────────────────────────────────────────
export async function logSystemError(
  error: unknown,
  context: LogContext = {},
): Promise<void> {
  // Flag anti-recursão: se o próprio log falhar, não tentamos logar o erro do log
  try {
    const errMessage =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : (() => { try { return JSON.stringify(error) } catch { return 'Erro desconhecido' } })()

    const stack = error instanceof Error ? (error.stack ?? null) : null
    const module = context.module ?? null
    const action = context.action ?? null

    // Rate limit + dedupe — early exit sem side-effects
    if (!_isAllowed(errMessage, module, action)) return

    // Não persistir em dev (a menos que VITE_OBSERVE_IN_DEV=true)
    if (!PERSIST_ENABLED) {
      // Em dev: só loga no console com formatação clara
      console.debug(
        `%c[observe] ${context.severity ?? 'medium'} | ${module ?? 'app'} | ${errMessage}`,
        'color: #6366f1; font-weight: bold',
      )
      return
    }

    const { id: userId, email: userEmail } = context.userId !== undefined
      ? { id: context.userId, email: context.userEmail ?? null }
      : await getCurrentUser()

    const ctxFromDom = readActiveContextFromDom()

    const payload = {
      company_id:      context.companyId ?? ctxFromDom.companyId,
      obra_id:         context.obraId ?? ctxFromDom.obraId,
      user_id:         userId,
      user_email:      userEmail,
      page:            context.page ?? (typeof window !== 'undefined' ? window.location.pathname : null),
      module,
      action,
      error_message:   String(errMessage).slice(0, 4000),
      error_code:      context.errorCode ?? null,
      stack_trace:     stack ? String(stack).slice(0, 8000) : null,
      endpoint:        context.endpoint ?? null,
      status_code:     context.statusCode ?? null,
      request_payload: safeStringifyPayload(context.requestPayload) ?? null,
      response_payload:safeStringifyPayload(context.responsePayload) ?? null,
      browser:         typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
      device:          detectDevice(),
      app_version:     APP_VERSION,
      environment:     ENV,
      severity:        context.severity ?? 'medium',
      status:          'open' as const,
    }

    // Fire-and-forget — não aguardamos resolução para não bloquear o caller.
    // .catch(() => {}) é OBRIGATÓRIO: impede que falhas de rede no insert criem
    // uma rejeição não-tratada, que acionaria window.unhandledrejection, que
    // chamaria logSystemError novamente — recursão indireta.
    void Promise.resolve(
      internalSupabase.from('system_error_logs').insert(payload as any)
    ).catch(() => { /* falha de log é silenciosa — nunca propaga */ })

  } catch {
    // NUNCA propaga — o logger não pode quebrar o app
  }
}

// ── Helpers especializados ────────────────────────────────────────

/**
 * Loga erros de chamadas Supabase (safeQuery/safeMutation/RPC).
 * Filtra por severidade: só persiste erros de servidor (5xx) e violações RLS.
 * Erros de validação (400, 409) são descartados — são erros de usuário.
 */
export async function logSupabaseError(
  result: { error: unknown | null },
  context: LogContext,
): Promise<void> {
  if (!result?.error) return
  const err = result.error as { message?: string; code?: string; details?: string; status?: number }

  const code = err?.code ?? ''
  const status = err?.status ?? context.statusCode ?? 0

  // Filtros: só logar erros que indicam problema sistêmico
  const isRlsViolation = code === '42501'                     // permission denied — RLS
  const isServerError  = status >= 500 && status < 600        // 5xx
  const isAuthError    = code === 'PGRST301' || status === 401 // session expirada
  const isNetworkError = !status && String(err?.message ?? '').toLowerCase().includes('fetch')

  if (!isRlsViolation && !isServerError && !isAuthError && !isNetworkError) return

  const severity: Severity =
    isRlsViolation ? 'high'
    : isServerError ? 'high'
    : isAuthError   ? 'medium'
    : 'medium'

  await logSystemError(err?.message || 'Supabase error', {
    ...context,
    severity: context.severity ?? severity,
    errorCode: code || context.errorCode || null,
    statusCode: status || context.statusCode || null,
    responsePayload: err,
  })
}

/**
 * Loga falhas de autenticação/sessão.
 * userId é omitido quando o usuário não está logado.
 */
export async function logAuthError(
  message: string,
  opts: { action: string; errorCode?: string; userId?: string | null } = { action: 'auth' },
): Promise<void> {
  await logSystemError(message, {
    module: 'AuthContext',
    action: opts.action,
    severity: 'high',
    errorCode: opts.errorCode ?? null,
    userId: opts.userId ?? null,
  })
}

/**
 * Loga erros de rede (fetch failures, timeouts) capturados em safeQuery/safeMutation.
 */
export async function logNetworkError(
  message: string,
  context: LogContext & { endpoint?: string; statusCode?: number },
): Promise<void> {
  await logSystemError(message, {
    ...context,
    module: context.module ?? 'network',
    action: context.action ?? 'fetch',
    severity: context.severity ?? 'high',
    endpoint: context.endpoint ?? null,
    statusCode: context.statusCode ?? null,
  })
}

// ── Listeners globais ─────────────────────────────────────────────
// Instalar uma única vez via App.tsx → installGlobalErrorListeners()
let _listenersInstalled = false

export function installGlobalErrorListeners(): void {
  if (_listenersInstalled || typeof window === 'undefined') return
  _listenersInstalled = true

  window.addEventListener('error', (event) => {
    const err = event.error || event.message
    // Chunk load failures já são capturados por lazyWithLog() — evitar dupla captura
    const msg = typeof err === 'string' ? err : (err as Error)?.message ?? ''
    if (/loading.*chunk|dynamically imported/i.test(msg)) return

    void logSystemError(err, {
      module: 'window.onerror',
      severity: 'high',
      action: 'global-error',
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const msg = reason instanceof Error ? reason.message : String(reason ?? '')
    // Ignorar rejeições de chunks (já tratadas em lazyWithLog)
    if (/loading.*chunk|dynamically imported|failed to fetch/i.test(msg)) return

    void logSystemError(reason ?? 'unhandledrejection', {
      module: 'window.unhandledrejection',
      severity: 'high',
      action: 'unhandled-promise',
    })
  })
}
